import Session       from '@/shared/services/session';
import Records       from '@/shared/services/records';
import Flash         from '@/shared/services/flash';
import EventBus      from '@/shared/services/event_bus';
import AbilityService from '@/shared/services/ability_service';
import LmoUrlService  from '@/shared/services/lmo_url_service';
import ScrollService  from '@/shared/services/scroll_service';
import openModal      from '@/shared/helpers/open_modal';
import { hardReload } from '@/shared/helpers/window';
import { downloadFile } from '@/shared/helpers/download_file';
import { subMonths } from 'date-fns';

export default new class TopicService {
  actions(topic) {
    return {
      subscribe: {
        name: 'common.action.subscribe',
        icon: 'mdi-bell',
        canPerform() {
          return !topic.lockedAt &&
          (topic.volume() === 'normal') &&
          topic.membersInclude(Session.user());
        },
        perform() {
          return openModal({
            component: 'ChangeVolumeForm',
            props: { model: topic }
          });
        }
      },

      unsubscribe: {
        name: 'common.action.unsubscribe',
        icon: 'mdi-bell-off',
        canPerform() {
          return (topic.volume() === 'loud') && topic.membersInclude(Session.user());
        },
        perform() {
          return openModal({
            component: 'ChangeVolumeForm',
            props: { model: topic }
          });
        }
      },

      unignore: {
        name: 'common.action.unignore',
        icon: 'mdi-bell-outline',
        canPerform() {
          return (topic.volume() === 'quiet') && topic.membersInclude(Session.user());
        },
        perform() {
          return openModal({
            component: 'ChangeVolumeForm',
            props: { model: topic }
          });
        }
      },

      start_vote: {
        name: 'activity_card.start_a_vote',
        icon: 'mdi-thumbs-up-down-outline',
        // collection: 'actions',
        canPerform() {
          return AbilityService.canStartPoll(topic);
        },
        perform() {
          EventBus.$emit('show-add-poll-form');
          ScrollService.scrollTo('#add-comment');
        }
      },

      add_comment: {
        name: 'comment_form.add_a_comment',
        icon: 'mdi-comment-outline',
        dockDisplay: 'icon',
        // collection: 'actions',
        canPerform() {
          return AbilityService.canAddComment(topic);
        },
        perform() {
          EventBus.$emit('show-add-comment-form');
          ScrollService.scrollTo('#add-comment');
          document.querySelector('#add-comment').focus();
        }
      },

      announce_thread: {
        name: 'invitation_form.invite_people',
        icon: 'mdi-account-multiple-plus',
        dock: 3,
        collection: 'members',
        canPerform() {
          return AbilityService.canAnnounceTopic(topic) || AbilityService.canAddGuestsTopic(topic);
        },
        perform() {
          return EventBus.$emit('openModal', {
            component: 'TopicMembersList',
            props: { topic }
          });
        }
      },

      seen_by: {
        name: 'discussion_context.seen_by_count',
        nameArgs: () => ({count: topic.seenByCount}),
        icon: 'mdi-eye-outline',
        collection: 'members',
        canPerform() { return topic.seenByCount > 0; },
        perform() {
          return openModal({
            component: 'SeenByModal',
            persistent: false,
            props: { topic }
          });
        }
      },

      users_notified: {
        name: 'discussion_context.count_notified',
        nameArgs: () => ({count: topic.usersNotifiedCount}),
        icon: 'mdi-bullhorn-outline',
        collection: 'members',
        canPerform() { return !!topic.usersNotifiedCount; },
        perform() {
          return openModal({
            component: 'AnnouncementHistory',
            persistent: false,
            props: { model: topic }
          });
        }
      },

      pin_thread: {
        icon: 'mdi-pin-outline',
        name: 'action_dock.pin_thread',
        canPerform() {
          return !topic.lockedAt && !topic.pinnedAt && (
            topic.adminsInclude(Session.user()) ||
            (topic.group().membersCanEditDiscussions && topic.membersInclude(Session.user()))
          );
        },
        perform: () => this.pin(topic)
      },

      unpin_thread: {
        icon: 'mdi-pin-off',
        name: 'action_dock.unpin_thread',
        canPerform() {
          return topic.pinnedAt && (
            topic.adminsInclude(Session.user()) ||
            (topic.group().membersCanEditDiscussions && topic.membersInclude(Session.user()))
          );
        },
        perform: () => this.unpin(topic)
      },

      dismiss_thread: {
        name: 'dashboard_page.mark_as_read',
        icon: 'mdi-check',
        dock: 1,
        canPerform() { return topic.isUnread(); },
        perform: () => this.dismiss(topic)
      },

      mark_as_read: {
        name: 'dashboard_page.mark_as_read',
        icon: 'mdi-email-open-outline',
        collection: 'actions',
        canPerform() { return topic.isUnread() && topic.membersInclude(Session.user()); },
        perform: () => this.markAllAsRead(topic)
      },

      export_thread: {
        name: 'common.action.print',
        icon: 'mdi-printer-outline',
        dock: 0,
        collection: 'actions',
        canPerform() {
          return !topic.discardedAt && topic.membersInclude(Session.user());
        },
        perform() {
          const topicable = topic.topicable();
          if (topicable.isA('poll')) {
            return hardReload(LmoUrlService.poll(topicable, {export: 1}, {action: 'export', ext: 'html', absolute: true}));
          } else {
            return hardReload(LmoUrlService.discussion(topicable, {export: 1}, {absolute: true, print: true}));
          }
        }
      },

      thread_markdown: {
        name: 'action_dock.markdown',
        icon: 'mdi-language-markdown-outline',
        dock: 0,
        collection: 'actions',
        canPerform() {
          return !topic.discardedAt && topic.membersInclude(Session.user());
        },
        menu: [
          {
            name: 'action_dock.copy_markdown',
            icon: 'mdi-content-copy',
            perform: () => this.copyMarkdown(topic)
          },
          {
            name: 'action_dock.download_markdown',
            icon: 'mdi-download',
            perform: () => this.downloadMarkdown(topic)
          }
        ]
      },

      edit_discussion: {
        name: 'common.action.edit',
        icon: 'mdi-pencil',
        // collection: 'actions',
        canPerform() {
          return topic.topicableType === 'Discussion' && AbilityService.canEditDiscussion(topic.topicable());
  },

        to() { return `/d/${topic.topicable().key}/edit`; }
      },

      edit_poll: {
        name: 'action_dock.edit_poll_type',
        nameArgs() { return {pollType: topic.topicable().translatedPollType()}; },
        icon: 'mdi-pencil',
        collection: 'actions',
        canPerform() {
          return topic.topicableType === 'Poll' && AbilityService.canEditPoll(topic.topicable());
        },
        to() { return `/p/${topic.topicable().key}/edit`; }
      },

      edit_tags: {
        icon: 'mdi-tag-plus-outline',
        name: 'loomio_tags.select_tags',
        canPerform() {
          return AbilityService.canEditTags(topic);
        },
        async perform() {
          let topicItem = topic.createdTopicItem();
          // Inbox previews omit timeline items. Resolve the opening item's
          // explicit assignments before rendering the editor.
          if (!topicItem) {
            try {
              await Records.topicItems.fetch({params: {topic_id: topic.id, sequence_id: 0}});
              topicItem = topic.createdTopicItem();
            } catch (error) {
              Flash.serverError(error);
              return;
            }
          }
          return openModal({
            component: 'TopicTagsDialog',
            persistent: false,
            props: { topic, topicItem }
          });
        }
      },

      move_thread: {
        name: 'action_dock.move_thread_v2',
        collection: 'actions',
        icon: 'mdi-file-move-outline',
        canPerform() { return AbilityService.canMoveTopic(topic); },
        perform() {
          return openModal({
            component: 'MoveTopicForm',
            props: { topic: topic.clone() }
          });
        }
      },


      lock_thread: {
        name: 'action_dock.lock_thread',
        collection: 'actions',
        icon: 'mdi-lock-outline',
        canPerform() {
          return !topic.lockedAt && topic.activePollsCount === 0 && (
            topic.adminsInclude(Session.user()) ||
            (topic.group().membersCanEditDiscussions && topic.membersInclude(Session.user()))
          );
        },
        perform: () => this.lock(topic)
      },

      unlock_thread: {
        name: 'action_dock.unlock_thread',
        collection: 'actions',
        icon: 'mdi-lock-open-outline',
        dock: 2,
        canPerform() {
          return topic.lockedAt && (
            topic.adminsInclude(Session.user()) ||
            (topic.group().membersCanEditDiscussions && topic.membersInclude(Session.user()))
          );
        },
        perform: () => this.unlock(topic)
      },

      thread_settings: {
        name: 'thread_arrangement_form.thread_settings',
        icon: 'mdi-cog-outline',
        collection: 'actions',
        canPerform() {
          return topic && topic.adminsInclude(Session.user());
        },
        perform() {
          return openModal({
            component: 'TopicForm',
            props: { topic: topic.clone() }
          });
        }
      },

      discard_thread: {
        name: 'action_dock.delete_thread',
        icon: 'mdi-delete-outline',
        collection: 'actions',
        canPerform() {
          return topic.adminsInclude(Session.user()) || (topic.author() === Session.user());
        },
        perform() {
          return openModal({
            component: 'ConfirmModal',
            props: {
              confirm: {
                submit: topic.discard,
                text: {
                  title: 'action_dock.delete_thread',
                  helptext: 'delete_discussion_form.body',
                  submit: 'action_dock.delete_thread',
                  flash: 'delete_discussion_form.discussion_deleted'
                },
                redirect: LmoUrlService.group(topic.group())
              }
            }
          });
        }
      }

    };
  }

  mute(topic, override) {
    if (override == null) { override = false; }
    if (!Session.user().hasExperienced("mutingThread") && !override) {
      Records.users.saveExperience("mutingThread");
      return Records.users.updateProfile(Session.user()).then(() => openModal({
        component: 'ConfirmModal',
        props: {
          confirm: {
            submit() {
              return topic.saveVolume(
                'quiet',
                'quiet',
                true
              );
            },
            text: {
              title: 'mute_explanation_modal.mute_thread',
              flash: 'discussion.volume.mute_message',
              fragment: 'mute_thread'
            }
          }
        }
      }));
    } else {
      const previousVolumes = { email: topic.readerVolumeEmail, push: topic.readerVolumePush };
      return topic.saveVolume(
        'quiet',
        'quiet'
      ).then(() => {
        return Flash.success("discussion.volume.mute_message",
          {name: topic.topicable().title}
        , 'undo', () => this.unmute(topic, previousVolumes));
      });
    }
  }

  unmute(topic, previousVolumes) {
    previousVolumes ||= { email: 'normal', push: 'quiet' };
    return topic.saveVolume(previousVolumes.email, previousVolumes.push).then(() => {
      return Flash.success("discussion.volume.unmute_message",
        {name: topic.topicable().title}
      , 'undo', () => this.mute(topic));
    });
  }

  lock(topic) {
    if (!Session.user().hasExperienced("closingThread")) {
      Records.users.saveExperience("closingThread");
      return Records.users.updateProfile(Session.user()).then(() => openModal({
        component: 'ConfirmModal',
        props: {
          confirm: {
            submit: topic.lock,
            text: {
              title: 'action_dock.lock_thread',
              helptext: 'lock_thread_modal.explanation',
              flash: 'discussion.locked.locked'
            }
          }
        }
      }));
    } else {
      return topic.lock().then(() => {
        return Flash.success("discussion.locked.locked");
      });
    }
  }

  unlock(topic) {
    return topic.unlock().then(() => {
      return Flash.success("discussion.locked.unlocked");
    });
  }

  markAllAsRead(topic) {
    return topic.markAllAsRead().then(() => {
      Flash.success("dashboard_page.discussion_marked_as_read");
    });
  }

  dismiss(topic) {
    return topic.dismiss().then(() => {
      return Flash.success("dashboard_page.discussion_marked_as_read", {}, 'undo', () => this.recall(topic));
    });
  }

  recall(topic) {
    return topic.recall().then(() => {
      return Flash.success("dashboard_page.discussion_marked_as_unread", {}, 'undo', () => this.dismiss(topic));
    });
  }

  pin(topic) {
    if (!Session.user().hasExperienced("pinningThread")) {
      return Records.users.saveExperience("pinningThread").then(() => openModal({
        component: 'ConfirmModal',
        props: {
          confirm: {
            submit: topic.savePin,
            text: {
              title: 'action_dock.pin_discussion',
              flash: 'discussion.pin.pinned',
              helptext: 'pin_discussion_modal.helptext'
            }
          }
        }
      }));
    } else {
      return topic.savePin().then(() => {
        return Flash.success("discussion.pin.pinned", 'undo', () => this.unpin(topic));
      });
    }
  }

  unpin(topic) {
    return topic.saveUnpin().then(() => {
      return Flash.success("discussion.pin.unpinned", 'undo', () => this.pin(topic));
    });
  }

  fetchMarkdown(topic) {
    return fetch(`/api/v1/topics/${topic.id}/markdown`).then(response => {
      if (!response.ok) { throw new Error('Could not load thread Markdown'); }
      return response.json();
    }).then(json => json.markdown);
  }

  // The clipboard write starts in the click handler with a pending blob, so
  // browsers that require a user gesture still allow it after the fetch.
  copyMarkdown(topic) {
    const blob = this.fetchMarkdown(topic).then(markdown => new Blob([markdown], {type: 'text/plain'}));
    return navigator.clipboard.write([new ClipboardItem({'text/plain': blob})])
      .then(() => Flash.success('action_dock.thread_markdown_copied'))
      .catch(error => {
        console.error(error);
        Flash.error('common.something_went_wrong');
      });
  }

  // Strip only characters that filesystems reject, so titles in any script
  // keep their words in the filename.
  downloadMarkdown(topic) {
    const name = topic.title.replace(/[\/\\:*?"<>|\u0000-\u001f]+/g, '').trim();
    return this.fetchMarkdown(topic)
      .then(markdown => downloadFile(markdown, 'text/markdown', `${name || 'thread'}.md`))
      .catch(error => {
        console.error(error);
        Flash.error('common.something_went_wrong');
      });
  }
};
